/**
 * Controls the Filter & Sort drawer visibility.
 *
 * Mirrors the megamenu approach: a body class (`filter-drawer-open`) drives the
 * slide-out, toggled from any `.plp-filter-toggle` button. The facet form inside
 * the drawer (facets.js) handles the actual filtering/sorting without a reload.
 *
 * Dismisses on the close button, overlay click and the Escape key.
 */
const BODY_CLASS = 'filter-drawer-open';

function getDrawer() {
  return document.querySelector('[data-filter-drawer]');
}

/**
 * The drawer lives in its own section, so its facet form doesn't know the
 * product-grid section id. Wire it up from the `results-list` element so
 * applying a filter re-renders the grid (not the drawer).
 */
function wireGridSection() {
  const gridSectionId = document.querySelector('results-list[section-id]')?.getAttribute('section-id');
  if (!gridSectionId) return;

  for (const form of document.querySelectorAll('[data-grid-section-form]')) {
    if (!form.getAttribute('section-id')) form.setAttribute('section-id', gridSectionId);
  }
}

wireGridSection();
document.addEventListener('DOMContentLoaded', wireGridSection);

const ACTIVE_FILTERS_CLASS = 'has-active-filters';
const FOOTER_ACTIVE_CLASS = 'is-active';

/**
 * True when the drawer has at least one active filter — a checked facet control or a
 * price range with a value. The always-selected sort radios (`name="sort_by"`) don't count.
 *
 * @param {Element} drawer
 * @returns {boolean}
 */
function hasActiveFilters(drawer) {
  for (const control of drawer.querySelectorAll('.filter-drawer__control')) {
    if (control instanceof HTMLInputElement && control.name !== 'sort_by' && control.checked) return true;
  }

  for (const priceInput of drawer.querySelectorAll('input[name^="filter.v.price"]')) {
    if (priceInput instanceof HTMLInputElement && priceInput.value.trim() !== '') return true;
  }

  return false;
}

/**
 * Reflects the active-filter state on the footer (`is-active`) and the form wrapper
 * (`has-active-filters`). The drawer isn't re-rendered when filters change — only the
 * product grid is — so we keep these in sync on the client instead of on reload.
 */
function syncActiveFilterState() {
  const drawer = getDrawer();
  if (!drawer) return;

  const active = hasActiveFilters(drawer);
  drawer.querySelector('.filter-drawer__footer')?.classList.toggle(FOOTER_ACTIVE_CLASS, active);
  drawer.querySelector('.filter-drawer__form-wrapper')?.classList.toggle(ACTIVE_FILTERS_CLASS, active);
}

// Recompute whenever a control inside the drawer is selected, toggled, or typed into.
for (const type of ['change', 'input']) {
  document.addEventListener(type, (event) => {
    if (event.target instanceof Element && event.target.closest('[data-filter-drawer]')) {
      syncActiveFilterState();
    }
  });
}

syncActiveFilterState();
document.addEventListener('DOMContentLoaded', syncActiveFilterState);

/* ------------------------------------------------------------------ *
 * Live drawer state — View Items count + unavailable options.
 *
 * The facet form only re-renders the product grid, so this drawer's
 * "View Items (N)" count and each option's availability go stale after a
 * filter change. On every filter update we re-fetch this drawer's section
 * HTML for the current (filtered) URL and patch just those bits in place —
 * without touching open <details>, scroll position, or checked states.
 * ------------------------------------------------------------------ */

// Matches ThemeEvents.FilterUpdate (assets/events.js); it bubbles to document.
const FILTER_UPDATE_EVENT = 'filter:update';

function getDrawerSectionId() {
  const section = getDrawer()?.closest('.shopify-section');
  return section?.id.replace(/^shopify-section-/, '') ?? null;
}

let refreshTimer;
/** @type {AbortController | undefined} */
let refreshController;

function scheduleDrawerRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(refreshDrawerState, 250);
}

async function refreshDrawerState() {
  const drawer = getDrawer();
  const sectionId = getDrawerSectionId();
  if (!drawer || !sectionId) return;

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);

  // Only the latest refresh should win.
  refreshController?.abort();
  refreshController = new AbortController();

  let html;
  try {
    const response = await fetch(url.toString(), { signal: refreshController.signal });
    if (!response.ok) return;
    html = await response.text();
  } catch {
    return; // aborted or network error — ignore
  }

  const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-filter-drawer]');
  if (!fresh) return;

  // 1. "View Items (N)" count.
  const freshSubmit = fresh.querySelector('.filter-drawer__submit');
  const submit = drawer.querySelector('.filter-drawer__submit');
  if (freshSubmit && submit) submit.textContent = freshSubmit.textContent.trim();

  // 2. Option availability — fade + disable impossible combinations.
  const freshDisabled = new Map();
  for (const option of fresh.querySelectorAll('.filter-drawer__option')) {
    const input = option.querySelector('input');
    if (input instanceof HTMLInputElement) freshDisabled.set(`${input.name}|${input.value}`, input.disabled);
  }

  for (const option of drawer.querySelectorAll('.filter-drawer__option')) {
    const input = option.querySelector('input');
    if (!(input instanceof HTMLInputElement)) continue;

    const disabled = freshDisabled.get(`${input.name}|${input.value}`);
    if (disabled === undefined) continue;

    // Never disable a checked input — disabled controls are dropped from the
    // form's FormData, which would silently remove that active filter next change.
    const shouldDisable = disabled && !input.checked;
    option.classList.toggle('filter-drawer__option--disabled', shouldDisable);
    input.disabled = shouldDisable;
  }
}

document.addEventListener(FILTER_UPDATE_EVENT, scheduleDrawerRefresh);

function openDrawer() {
  if (document.body.classList.contains(BODY_CLASS)) return;

  document.body.classList.add(BODY_CLASS);
  document.documentElement.setAttribute('scroll-lock', '');
  getDrawer()?.setAttribute('aria-hidden', 'false');
}

function closeDrawer() {
  if (!document.body.classList.contains(BODY_CLASS)) return;

  document.body.classList.remove(BODY_CLASS);
  document.documentElement.removeAttribute('scroll-lock');
  getDrawer()?.setAttribute('aria-hidden', 'true');
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('.plp-filter-toggle')) {
    event.preventDefault();
    openDrawer();
    return;
  }

  if (target.closest('[data-filter-drawer-close]') || target.closest('[data-filter-drawer-overlay]')) {
    event.preventDefault();
    closeDrawer();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeDrawer();
});
